/**
 * Responsive text field placeholder textbased on viewport width
 * Text field attr placeholder text will toggle based on viewport width
 * @module responsivePlaceholder
 */

import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';
import debounce from 'lodash/debounce';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'responsivePlaceholder';
export const DEFAULTS = {
  placeholderTablet: null,
  placeholderBreakpoint: '1000',
  debounceDelay: 250, // Add debounce to resize event
};


/**
 * Initializes responsivePlaceholder
 * @param {HTMLElement} el - input element
 * @param {Object} options - Options used for customizing the responsive placeholder
 * @see DEFAULTS
 * @returns {Object|boolean} - Returns plugin instance or false if initialization fails
 */
export default function responsivePlaceholder(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const { placeholderTablet, placeholderBreakpoint, debounceDelay } = opts;
  
  // Store the original desktop placeholder from the element
  opts.placeholderDesktop = $el.attr('placeholder');

  // Return false if tablet placeholder is not provided
  if (placeholderTablet === null) return false;

  // Update placeholder based on current screen width
  function updatePlaceholder() {
    const windowWidth = window.innerWidth;
    const newPlaceholder = windowWidth <= placeholderBreakpoint
      ? placeholderTablet
      : opts.placeholderDesktop;
    
    // Only update if placeholder has changed
    if ($el.attr('placeholder') !== newPlaceholder) {
      $el.attr('placeholder', newPlaceholder);
    }
  }

  // Create debounced update function
  const debouncedUpdatePlaceholder = debounce(updatePlaceholder, debounceDelay);

  // Initialize
  updatePlaceholder();

  // Set up event listener
  $(window).on(`resize.${PLUGIN_NAME}`, debouncedUpdatePlaceholder);

  // Build the public API
  const api = {
    // Update placeholders immediately
    update: updatePlaceholder,
    
    // Destroy the plugin instance and clean up
    destroy: () => {
      $(window).off(`resize.${PLUGIN_NAME}`);
      $(el).attr('placeholder', opts.placeholderDesktop);
      return null;
    }
  };

  // Store the API on the element's data attribute
  $el.data(PLUGIN_NAME, api);

  return api;
}

registerJQueryPlugin(PLUGIN_NAME, responsivePlaceholder);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/responsive-placeholder.js